import React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../utils/cn.ts';
import { ComboboxPopover } from './ComboboxPopover.tsx';
import { ComboboxTrigger } from './ComboboxTrigger.tsx';
import type { ComboboxOption, ComboboxRenderState } from './Combobox.types.ts';
import { useComboboxController } from './useComboboxController.ts';

export type { ComboboxOption } from './Combobox.types.ts';

interface ComboboxProps<TValue extends string = string> {
    label?: string;
    ariaLabel?: string;
    options: ComboboxOption<TValue>[];
    value?: TValue | null;
    onChange: (value: TValue, option: ComboboxOption<TValue>) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    emptyText?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    align?: 'start' | 'end';
    leftIcon?: string;
    className?: string;
    triggerClassName?: string;
    popoverClassName?: string;
    listClassName?: string;
    optionClassName?: string;
    maxListHeight?: string;
    preserveListScroll?: boolean;
    filterOption?: (option: ComboboxOption<TValue>, query: string) => boolean;
    renderOption?: (
        option: ComboboxOption<TValue>,
        state: ComboboxRenderState
    ) => React.ReactNode;
}

export const Combobox = <TValue extends string = string>({
    label,
    ariaLabel,
    options,
    value,
    onChange,
    placeholder = 'Select...',
    searchPlaceholder = 'Search...',
    emptyText = 'No matching options',
    disabled = false,
    fullWidth = true,
    align = 'end',
    leftIcon,
    className,
    triggerClassName,
    popoverClassName,
    listClassName,
    optionClassName,
    maxListHeight = '320px',
    preserveListScroll = false,
    filterOption,
    renderOption,
}: ComboboxProps<TValue>) => {
    const [savedListScrollTop, setSavedListScrollTop] = React.useState(0);
    const {
        activeOptionId,
        activeOptionIndex,
        close,
        controlName,
        enabled,
        filteredOptions,
        handlePopoverKeyDown,
        handleRootBlur,
        ids: { listboxId, triggerId },
        isOpen,
        open,
        query,
        refs: { popoverRef, rootRef, searchInputRef, triggerRef },
        selectedOption,
        selectOption,
        setActiveIndex,
        setQuery,
    } = useComboboxController({
        ariaLabel,
        disabled,
        filterOption,
        label,
        onChange,
        options,
        value,
    });
    const triggerText = selectedOption?.label ?? placeholder;
    const popoverStyle = useComboboxPopoverPosition({
        align,
        fullWidth,
        isOpen,
        rootRef,
    });
    const shouldRestoreListScroll = preserveListScroll && query.trim() === '' && savedListScrollTop > 0;
    const popover = isOpen ? (
        <ComboboxPopover
            activeOptionId={activeOptionId}
            activeOptionIndex={activeOptionIndex}
            align={align}
            controlName={controlName}
            emptyText={emptyText}
            filteredOptions={filteredOptions}
            fullWidth={fullWidth}
            initialListScrollTop={shouldRestoreListScroll ? savedListScrollTop : 0}
            listClassName={listClassName}
            listboxId={listboxId}
            maxListHeight={maxListHeight}
            optionClassName={optionClassName}
            placementStyle={popoverStyle}
            popoverClassName={popoverClassName}
            popoverRef={popoverRef}
            query={query}
            renderOption={renderOption}
            searchInputRef={searchInputRef}
            searchPlaceholder={searchPlaceholder}
            selectedValue={value}
            onActiveIndexChange={setActiveIndex}
            onKeyDown={handlePopoverKeyDown}
            onListScrollTopChange={(scrollTop) => {
                if (preserveListScroll && query.trim() === '') {
                    setSavedListScrollTop(scrollTop);
                }
            }}
            onQueryChange={(nextQuery) => {
                if (preserveListScroll && nextQuery.trim() !== '') {
                    setSavedListScrollTop(0);
                }
                setQuery(nextQuery);
                setActiveIndex(-1);
            }}
            onSelect={selectOption}
            preserveListScroll={preserveListScroll}
            skipInitialActiveScroll={shouldRestoreListScroll}
        />
    ) : null;

    return (
        // biome-ignore lint/a11y/noStaticElementInteractions: The wrapper only observes focus leaving the composite combobox; the trigger and listbox own interaction semantics.
        // biome-ignore lint/a11y/noNoninteractiveElementInteractions: The wrapper only observes focus leaving the composite combobox; the trigger and listbox own interaction semantics.
        <div
            ref={rootRef}
            onBlur={handleRootBlur}
            className={cn('relative inline-flex min-w-0', fullWidth && 'w-full', className)}
        >
            <ComboboxTrigger
                ref={triggerRef}
                id={triggerId}
                controlName={controlName}
                enabled={enabled}
                fullWidth={fullWidth}
                isOpen={isOpen}
                leftIcon={leftIcon}
                listboxId={listboxId}
                selected={Boolean(selectedOption)}
                text={triggerText}
                triggerClassName={triggerClassName}
                onClose={() => close()}
                onOpen={open}
            />

            {popover && typeof document !== 'undefined'
                ? createPortal(popover, document.body)
                : popover}
        </div>
    );
};

function useComboboxPopoverPosition({
    align,
    fullWidth,
    isOpen,
    rootRef,
}: {
    align: 'start' | 'end';
    fullWidth: boolean;
    isOpen: boolean;
    rootRef: React.RefObject<HTMLDivElement | null>;
}): React.CSSProperties | undefined {
    const [style, setStyle] = React.useState<React.CSSProperties>();

    React.useLayoutEffect(() => {
        if (!isOpen) {
            setStyle(undefined);
            return;
        }

        const updatePosition = () => {
            const root = rootRef.current;
            if (!root) {
                setStyle({ visibility: 'hidden' });
                return;
            }

            const rect = root.getBoundingClientRect();
            const viewportPadding = 16;
            const availableWidth = Math.max(0, window.innerWidth - viewportPadding * 2);
            const preferredWidth = Math.min(360, availableWidth);
            const minimumWidth = fullWidth ? Math.min(rect.width, availableWidth) : 0;
            const width = Math.min(availableWidth, Math.max(minimumWidth, preferredWidth));
            const unclampedLeft = align === 'start' ? rect.left : rect.right - width;
            const maxLeft = Math.max(viewportPadding, window.innerWidth - width - viewportPadding);
            const left = Math.min(Math.max(viewportPadding, unclampedLeft), maxLeft);

            setStyle({
                left,
                top: rect.bottom + 4,
                width,
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [align, fullWidth, isOpen, rootRef]);

    return style ?? { visibility: 'hidden' };
}
